(() => {
if (customElements.get('cart-drawer')) return;

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => {
      if (evt.code === 'Escape') this.close();
    });

    this.addEventListener('click', (event) => {
      if (event.target.id === 'CartDrawer-Overlay') this.close();
    });

    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    if (document.documentElement.dataset.marabuCartClickBound === 'true') return;
    document.documentElement.dataset.marabuCartClickBound = 'true';

    document.addEventListener('click', (event) => {
      const cartLink = event.target.closest?.('#cart-icon-bubble');
      const drawer = document.querySelector('cart-drawer');
      if (!cartLink || !drawer) return;
      event.preventDefault();
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.setAttribute('aria-controls', 'CartDrawer');
      drawer.open(cartLink);
    });

    document.addEventListener('keydown', (event) => {
      const cartLink = event.target.closest?.('#cart-icon-bubble');
      const drawer = document.querySelector('cart-drawer');
      if (!cartLink || !drawer || !['Enter', ' '].includes(event.key)) return;
      event.preventDefault();
      drawer.open(cartLink);
    });
  }

  async open(triggeredBy) {
    if (triggeredBy) {
      this.setActiveElement(triggeredBy);
    }

    this.classList.add('animate', 'active');
    document.body.classList.add('overflow-hidden');
    await this.refreshDrawerFromShopify();

    const note = this.querySelector('[id^="Details-"] summary');

    if (note && !note.hasAttribute('role')) {
      this.setSummaryAccessibility(note);
    }

    requestAnimationFrame(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener('transitionend', () => {
      const container = this.classList.contains('is-empty')
        ? this.querySelector('.drawer__inner-empty')
        : this.querySelector('#CartDrawer') || this;

      const focusElement =
        this.querySelector('.drawer__inner') ||
        this.querySelector('.drawer__close');

      if (
        container &&
        focusElement &&
        typeof trapFocus === 'function'
      ) {
        trapFocus(container, focusElement);
      }
    }, { once: true });

  }

  async refreshDrawerFromShopify() {
    const root = window.Shopify?.routes?.root || '/';

    try {
      // Render the drawer from the cart page context so Liquid sees the latest cart.items.
      const sectionUrl = `${root}cart?section_id=cart-drawer&_cart_refresh=${Date.now()}`;
      const sectionResponse = await fetch(sectionUrl, {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'text/html' }
      });

      if (sectionResponse.ok) {
        const html = await sectionResponse.text();
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const freshDrawer = parsed.querySelector('#CartDrawer');
        const currentDrawer = this.querySelector('#CartDrawer');

        if (freshDrawer && currentDrawer) {
          currentDrawer.innerHTML = freshDrawer.innerHTML;
        }
      }

      // The cart endpoint is authoritative for quantity count and empty state.
      const cartResponse = await fetch(`${root}cart.js?_cart_refresh=${Date.now()}`, {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' }
      });

      if (!cartResponse.ok) return;
      const cart = await cartResponse.json();
      const empty = Number(cart.item_count) === 0;

      this.classList.toggle('is-empty', empty);
      const inner = this.querySelector('.drawer__inner');
      if (inner) inner.classList.toggle('is-empty', empty);

      const countLabel = this.querySelector('.marabu-cart-count-label');
      if (countLabel) {
        countLabel.textContent = `${cart.item_count} ${cart.item_count === 1 ? 'Item' : 'Items'}`;
      }

      if (typeof window.refreshMarabuCartCount === 'function') {
        window.refreshMarabuCartCount(cart.item_count);
      } else {
        const count = document.querySelector('#marabu-cart-count');
        if (count) {
          count.textContent = String(cart.item_count);
          count.style.display = cart.item_count > 0 ? 'flex' : 'none';
        }
      }

      const bubble = document.querySelector('#cart-icon-bubble .cart-count-bubble');
      if (bubble) {
        if (empty) {
          bubble.remove();
        } else {
          let visible = bubble.querySelector('span[aria-hidden="true"]');
          if (!visible && cart.item_count < 100) {
            visible = document.createElement('span');
            visible.setAttribute('aria-hidden', 'true');
            bubble.prepend(visible);
          }
          if (visible) visible.textContent = String(cart.item_count);
          const hidden = bubble.querySelector('.visually-hidden');
          if (hidden) hidden.textContent = `Cart contains ${cart.item_count} ${cart.item_count === 1 ? 'item' : 'items'}`;
        }
      }
    } catch (error) {
      console.warn('[CartDrawer] Could not refresh drawer before opening.', error);
    }
  }

  close() {
    this.classList.remove('active');

    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.activeElement);
    }

    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(note) {
    note.setAttribute('role', 'button');
    note.setAttribute('aria-expanded', 'false');

    const next = note.nextElementSibling;

    if (next && next.id) {
      note.setAttribute('aria-controls', next.id);
    }

    note.addEventListener('click', (event) => {
      const details = event.currentTarget.closest('details');

      if (details) {
        event.currentTarget.setAttribute(
          'aria-expanded',
          String(!details.hasAttribute('open'))
        );
      }
    });

    if (typeof onKeyUpEscape === 'function') {
      note.parentElement.addEventListener('keyup', onKeyUpEscape);
    }
  }

  renderContents(parsedState) {
    if (!parsedState || !parsedState.sections) {
      console.error(
        '[CartDrawer] Shopify did not return section HTML.',
        parsedState
      );

      window.location.href =
        window.routes?.cart_url || '/cart';

      return;
    }

    const drawerInner = this.querySelector('.drawer__inner');

    if (drawerInner) {
      drawerInner.classList.remove('is-empty');
    }

    this.productId = parsedState.id || null;

    this.getSectionsToRender().forEach((section) => {
      const html = parsedState.sections[section.id];

      if (!html) {
        console.warn(
          '[CartDrawer] Missing section:',
          section.id
        );
        return;
      }

      const target = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!target) {
        console.warn(
          '[CartDrawer] Target not found:',
          section.selector || section.id
        );
        return;
      }

      const parsed = new DOMParser().parseFromString(
        html,
        'text/html'
      );

      const source = section.selector
        ? parsed.querySelector(section.selector)
        : parsed.getElementById(section.id);

      if (!source) {
        console.warn(
          '[CartDrawer] Could not find source section:',
          section.id
        );
        return;
      }

      // Refresh the target without removing its outer element.
      target.innerHTML = source.innerHTML;
    });

    const overlay = this.querySelector('#CartDrawer-Overlay');

    if (overlay) {
      overlay.addEventListener(
        'click',
        this.close.bind(this)
      );
    }

    this.open();
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const parsed = new DOMParser().parseFromString(
      html,
      'text/html'
    );

    const element = parsed.querySelector(selector);

    return element ? element.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
        section: 'cart-drawer'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    const parsed = new DOMParser().parseFromString(
      html,
      'text/html'
    );

    return parsed.querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return super.getSectionsToRender();
  }
}

if (!customElements.get('cart-drawer-items')) {
  customElements.define('cart-drawer-items', CartDrawerItems);
}
})();
